import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { type ClientUser, DEFAULT_NOTIFICATION_TEMPLATE_ID, type UserModel, UserStatus } from '@models';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private http = inject(HttpClient);

  getClientUsersList(clientId: string): Observable<ClientUser[]> {
    return this.http
      .get<{ data: ClientUser[]; }>(`clients/v1/clients/${clientId}/users`)
      .pipe(map(data => data.data
        .map(user => ({
          ...user,
          isArchived: user.isArchived || user.status === UserStatus.ARCHIVED,
          notificationTemplateId: user.notificationTemplateId || DEFAULT_NOTIFICATION_TEMPLATE_ID,
        })),
      ));
  }

  addClientUser(clientId: string, newUser: ClientUser): Observable<ClientUser> {
    return this.http
      .post<{ data: ClientUser; }>(`clients/v1/clients/${clientId}/users`, newUser)
      .pipe(map(({ data }) => data));
  }

  updatePersonalUserData<T = UserModel>(data: Partial<T>) {
    return this.http.patch<{ data: T; }>('clients/v1/users/current', data).pipe(map(data => data.data));
  }

  updateUserData<T = ClientUser>(clientId: string, userId: string, data: Partial<T>) {
    return this.http
      .patch<{ data: T; }>(`clients/v1/clients/${clientId}/users/${userId}`, data)
      .pipe(map(data => data.data));
  }

  resetPassword(clientId: string, userId: string) {
    return this.http.get(`clients/v1/clients/${clientId}/users/${userId}/reset-password`);
  }

  resendInvitation(clientId: string, userId: string) {
    return this.http.get(`clients/v1/clients/${clientId}/users/${userId}/resend-invite`);
  }

  resetTotp(clientId: string, userId: string) {
    return this.http.post(`clients/v1/clients/${clientId}/users/${userId}/totp/reset`, {});
  }
}
